import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { fa } from "../i18n/fa";
import { prefersReducedMotion } from "./card-motion";
import { supports3DTransforms } from "./supports-3d";

/* ------------------------------------------------------------------ *
 * A REAL 3D die. The previous implementation rotated a FLAT span with
 * no `perspective` ancestor and no `transform-style: preserve-3d`, so
 * `rotateX(690deg)` degenerated to a vertical squash — it read as a
 * card flipping, not a die tumbling.
 *
 * Here the die is an actual cube: six faces pushed out to `size/2`
 * along their own normals, opposite faces summing to 7 as on a real
 * die. `restOrientation` returns the cube rotation that brings a given
 * value to the camera, so the die always SETTLES on the number the
 * server rolled — the animation never implies a different result.
 * ------------------------------------------------------------------ */

/** The die is thrown for this long; it first touches the felt at DIE_CONTACT_MS. */
export const DIE_THROW_MS = 1000;
export const DIE_CONTACT_MS = 700;

/** The standard 1–6 pip layout on a 3×3 grid (cells 0..8, row-major). */
export const PIP_LAYOUT: Record<number, readonly number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

export type FaceName = "front" | "back" | "right" | "left" | "top" | "bottom";

/** Which value each cube face carries. Opposite faces sum to 7, as on a real die. */
export const FACE_VALUE: Record<FaceName, number> = {
  front: 1,
  back: 6,
  right: 3,
  left: 4,
  top: 2,
  bottom: 5,
};

export const FACES: readonly FaceName[] = ["front", "back", "right", "left", "top", "bottom"];

/**
 * Per-face tone for a die lit from above and slightly in front. Ivory stays ivory on the lit
 * faces; the ones turning away pick up a warm grey. This is what sells the solidity at small
 * sizes — see {@link Die3D}'s `shaded`.
 */
export const FACE_SHADE: Record<FaceName, string> = {
  top: "linear-gradient(160deg, #ffffff 0%, #f7f5ec 100%)",
  front: "linear-gradient(160deg, #fbfaf4 0%, #efece1 100%)",
  right: "linear-gradient(160deg, #ebe7da 0%, #dad5c6 100%)",
  left: "linear-gradient(160deg, #e4e0d2 0%, #d0cabb 100%)",
  back: "linear-gradient(160deg, #e0dbcd 0%, #ccc6b6 100%)",
  bottom: "linear-gradient(160deg, #d6d0c0 0%, #c2bbaa 100%)",
};

/** Places a face on the surface of a cube of side `size`. */
export function faceTransform(face: FaceName, size: number): string {
  const h = size / 2;
  switch (face) {
    case "front":
      return `translateZ(${h}px)`;
    case "back":
      return `rotateY(180deg) translateZ(${h}px)`;
    case "right":
      return `rotateY(90deg) translateZ(${h}px)`;
    case "left":
      return `rotateY(-90deg) translateZ(${h}px)`;
    case "top":
      return `rotateX(90deg) translateZ(${h}px)`;
    case "bottom":
      return `rotateX(-90deg) translateZ(${h}px)`;
  }
}

/** The cube rotation that brings `value` to face the camera — the inverse of its face transform. */
export function restOrientation(value: number): { x: number; y: number } {
  switch (value) {
    case FACE_VALUE.back:
      return { x: 0, y: 180 };
    case FACE_VALUE.right:
      return { x: 0, y: -90 };
    case FACE_VALUE.left:
      return { x: 0, y: 90 };
    case FACE_VALUE.top:
      return { x: -90, y: 0 };
    case FACE_VALUE.bottom:
      return { x: 90, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
}

/** A tiny deterministic hash → per-die throw variance. Deterministic so it is testable and so a
 *  re-render never re-rolls the look of a die that has already landed. */
export function throwVariance(seed: string): { turns: number; tilt: number; drift: number } {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const a = (h >>> 0) % 1000;
  const b = (h >>> 10) % 1000;
  const c = (h >>> 20) % 1000;
  return {
    turns: 2 + (a % 2), // 2..3 full tumbles (4 spun the cube harder than it read, for extra cost)
    tilt: Math.round((b / 999) * 24) - 12, // −12°..+12° resting tilt
    drift: Math.round((c / 999) * 36) - 18, // −18..+18px lateral drift
  };
}

/**
 * The faces a die shows WHILE it tumbles, as a per-throw random sequence.
 *
 * A real die does not present each face once in order — it repeats some and skips others. It
 * also never shows the SAME face twice running, because that reads as the die freezing rather
 * than turning, so consecutive duplicates are nudged on.
 *
 * Seeded from the throw's own `seed` (which changes every roll), so the sequence is different
 * each time yet still deterministic for a given throw — the same roll replays identically on a
 * resize or a re-render.
 */
export function tumbleSequence(seed: string, slots: number): number[] {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let x = h >>> 0 || 1;
  const out: number[] = [];
  for (let n = 0; n < slots; n += 1) {
    // xorshift32 — cheap, and plenty for six faces.
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    let face = (x % 6) + 1;
    const prev = out[out.length - 1];
    if (prev !== undefined && face === prev) face = (face % 6) + 1;
    out.push(face);
  }
  return out;
}

/** The throw: launched from the roller's edge, tumbling, contact + bounce, settling flat on
 *  `value`. `translateY` is monotonic toward 0 so it reads as a throw that lands. */
export function throwKeyframes(
  value: number,
  fromBottom: boolean,
  v: { turns: number; drift: number; launch?: number; zSpin?: number },
): Keyframe[] {
  const rest = restOrientation(value);
  // How far the die is thrown from. Scaled by the caller to the board it is landing on — a fixed
  // 120px launch threw the die in from OFF the felt once the board was shortened.
  const reach = v.launch ?? 90;
  const y0 = fromBottom ? reach : -reach;
  const spin = 360 * v.turns;
  // OPTIONAL third axis. Rotating on X and Y alone reads as a cube being turned; adding Z makes
  // it read as a die actually thrown. Defaults to 0, so every existing caller (backgammon) keeps
  // byte-identical keyframes — only a caller that asks for it gets the extra axis.
  const z = v.zSpin ?? 0;
  const at = (x: number, y: number, rx: number, ry: number, s: number, rz = 0): string =>
    `translate3d(${x}px, ${y}px, 0) rotateX(${rx}deg) rotateY(${ry}deg) rotateZ(${rz}deg) scale(${s})`;
  return [
    { offset: 0, transform: at(v.drift, y0, rest.x - spin, rest.y - spin, 0.55, -z), opacity: 0 },
    { offset: 0.08, opacity: 1 },
    {
      offset: 0.35,
      transform: at(
        Math.round(v.drift * 0.4),
        Math.round(y0 * 0.28),
        rest.x - 150,
        rest.y - 190,
        1.12,
        Math.round(-z * 0.45),
      ),
    },
    // contact with the felt
    {
      offset: 0.7,
      transform: at(0, 0, rest.x - 26, rest.y - 30, 1, Math.round(-z * 0.1)),
      easing: "ease-out",
    },
    // a low bounce
    { offset: 0.8, transform: at(0, fromBottom ? -12 : 12, rest.x - 8, rest.y - 10, 0.98) },
    { offset: 0.92, transform: at(0, 0, rest.x, rest.y, 1.02) },
    { offset: 1, transform: at(0, 0, rest.x, rest.y, 1) },
  ];
}

function Pips({ value, size }: { value: number; size: number }): ReactNode {
  const filled = new Set(PIP_LAYOUT[value] ?? []);
  const dot = Math.max(3, Math.round(size * 0.16));
  return (
    <span
      aria-hidden
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gridTemplateRows: "repeat(3, 1fr)",
        width: "72%",
        height: "72%",
        gap: 1,
      }}
    >
      {Array.from({ length: 9 }, (_, i) => (
        <span
          key={i}
          style={
            filled.has(i)
              ? {
                  alignSelf: "center",
                  justifySelf: "center",
                  width: dot,
                  height: dot,
                  borderRadius: "50%",
                  background: "#1a0f0a",
                  boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.35)",
                }
              : undefined
          }
        />
      ))}
    </span>
  );
}

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const;
function faNum(value: number): string {
  return String(value).replace(/\d/g, (d) => FA_DIGITS[Number(d)] ?? d);
}

/**
 * A die's accessible name must not give the result away before the die has landed.
 *
 * `aria-label` was the final value from the FIRST frame of the throw, so a screen reader read out
 * the number a full second before it settled — the one user for whom the animation is the entire
 * point of the delay. While it tumbles the die announces that it is rolling; the value arrives when
 * it does on screen.
 *
 * Returns `false` while a throw is in flight. A die placed AT REST (a resume, a re-render, reduced
 * motion) has landed by definition, so it names its value immediately.
 */
function useDieLanded(flying: boolean): boolean {
  const [landed, setLanded] = useState(!flying);
  useEffect(() => {
    if (!flying) return undefined;
    const id = window.setTimeout(() => setLanded(true), DIE_THROW_MS);
    return () => window.clearTimeout(id);
    // Mount-once (deliberately empty deps), exactly like the throw itself: `flying` is already
    // latched by `useFlyingAtMount`, and re-running this on a prop change could only ever reveal
    // the value early. (No `eslint-disable` — `react-hooks/exhaustive-deps` is NOT configured in
    // this repo, and naming an unknown rule in a disable comment is itself a lint error.)
  }, []);
  return landed;
}

/**
 * WHETHER THIS DIE THROWS IS DECIDED AT MOUNT, AND NEVER RE-DECIDED.
 *
 * ⚠️ This is not tidiness — it is the fix for a throw that was started and then killed inside the
 * same commit. `DieCube` has always run its throw from a mount-once effect; `DieFlat` (the renderer
 * every platform actually gets, via `alwaysDrawn`) instead keyed its effect on the live `throwing`
 * prop. So the moment ANY re-render arrived with `throwing` back to `false` — which is exactly what
 * the opening-contest slot does, because the caller marks the die "thrown" as soon as it has landed
 * once — React ran the effect's cleanup and `cancel()`ed every animation still in flight.
 *
 * MEASURED (2026-08-15, the contest slot): 7 animations started (1 transform + 6 face layers) and
 * 7 cancelled, in the same commit. On screen that is a die with no throw at all.
 *
 * A die's throw plays when its NODE APPEARS: every caller either keys the die by the roll (the
 * playing tray, منچ) or mounts it the instant its value lands (the contest slot). Latching here
 * means an unrelated re-render can neither start a throw nor cancel one.
 */
function useFlyingAtMount(throwing: boolean): boolean {
  const reduced = prefersReducedMotion();
  return useRef(throwing && !reduced).current;
}

/** What a die is called right now: its value once it has settled, else «تاس در حال چرخش». */
function dieLabel(value: number, landed: boolean): string {
  return landed ? faNum(value) : fa.backgammon.dieRolling;
}

/**
 * One die. `throwing` plays the ~1s throw exactly once on mount (key this by the roll so a fresh
 * roll remounts it); otherwise the die is placed at rest. Reduced motion / no WAAPI → at rest.
 */
/**
 * A die for engines that FLATTEN CSS 3-D (several Android WebViews accept `preserve-3d` and
 * then composite the subtree flat — see `supports-3d.ts`).
 *
 * It never relies on depth: the solidity is DRAWN — a lit top-left, a shaded right and bottom
 * edge, and a cast shadow — and the throw is a 2-D tumble that cycles through faces before
 * settling on the real value. On a device that flattens 3-D this looks like a die; the cube
 * does not.
 */
function DieFlat({
  value,
  size,
  seed,
  throwing,
  fromBottom,
  spent,
  launch,
  testId,
}: {
  value: number;
  size: number;
  seed: string;
  throwing: boolean;
  fromBottom: boolean;
  spent: boolean;
  launch?: number;
  testId?: string;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const layers = useRef<(HTMLSpanElement | null)[]>([]);
  const v = throwVariance(seed);
  const flying = useFlyingAtMount(throwing);
  const landed = useDieLanded(flying);

  useLayoutEffect(() => {
    if (!flying) return undefined;
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return undefined;
    const reach = launch ?? 90;
    const y0 = fromBottom ? reach : -reach;
    const spin = 360 * (v.turns + 1);
    const anims = [
      el.animate(
        [
          {
            offset: 0,
            transform: `translate(${v.drift}px, ${y0}px) rotate(${-spin}deg) scale(.55)`,
            opacity: 0,
          },
          { offset: 0.08, opacity: 1 },
          {
            offset: 0.4,
            transform: `translate(${Math.round(v.drift * 0.4)}px, ${Math.round(y0 * 0.25)}px) rotate(${Math.round(-spin * 0.45)}deg) scale(1.14)`,
          },
          { offset: 0.7, transform: "translate(0,0) rotate(-18deg) scale(1)", easing: "ease-out" },
          {
            offset: 0.82,
            transform: `translate(0, ${fromBottom ? -10 : 10}px) rotate(-6deg) scale(.97)`,
          },
          { offset: 1, transform: `translate(0,0) rotate(${v.tilt}deg) scale(1)` },
        ],
        { duration: DIE_THROW_MS, easing: "cubic-bezier(.25,.6,.3,1)", fill: "both" },
      ),
    ];
    // The face CYCLES while it tumbles, so the number is not known from the first frame.
    // Driven entirely by stacked opacity animations — NOT by React state. The earlier version
    // re-rendered every 90ms, which is exactly the kind of work a weak WebView cannot absorb
    // mid-animation; opacity on a handful of layers stays on the compositor.
    const cycleEnd = DIE_CONTACT_MS / DIE_THROW_MS;
    const slots = Math.max(1, Math.round(DIE_CONTACT_MS / 110));
    // A DIFFERENT face order every throw. The old schedule was `n % 5`, which produced the
    // identical sequence on every roll — after a few throws it read as a mechanism rather than
    // a die. Repeats and skips are welcome; a fixed pattern is not.
    const order = tumbleSequence(seed, slots);
    for (let i = 0; i < 6; i += 1) {
      const layer = layers.current[i];
      if (!layer) continue;
      const face = i + 1;
      const keyframes: Keyframe[] = [{ offset: 0, opacity: 0 }];
      // Every slot this face happens to win in the shuffled order.
      order.forEach((f, n) => {
        if (f !== face) return;
        const at = ((n + 0.5) / slots) * cycleEnd;
        keyframes.push({ offset: Math.max(0.001, at - 0.03), opacity: 0 });
        keyframes.push({ offset: at, opacity: 1 });
        keyframes.push({ offset: Math.min(cycleEnd, at + 0.03), opacity: 0 });
      });
      if (face === value) {
        // ...and the TRUE value, whatever it did during the tumble, is what it settles on.
        keyframes.push({ offset: Math.max(0.001, cycleEnd - 0.001), opacity: 0 });
        keyframes.push({ offset: cycleEnd, opacity: 1 });
        keyframes.push({ offset: 1, opacity: 1 });
      } else {
        keyframes.push({ offset: 1, opacity: 0 });
      }
      // Offsets must be non-decreasing for WAAPI.
      keyframes.sort((a, b) => (a.offset as number) - (b.offset as number));
      anims.push(layer.animate(keyframes, { duration: DIE_THROW_MS, fill: "both" }));
    }
    return () => anims.forEach((a) => a.cancel());
    // MOUNT-ONCE (deliberately empty deps), exactly like `DieCube` below. A live dep list let a
    // single unrelated re-render cancel a throw mid-flight — see `useFlyingAtMount`.
  }, []);

  const r = Math.max(4, Math.round(size * 0.18));
  const lip = Math.max(2, Math.round(size * 0.09)); // the visible thickness of the block
  return (
    <span
      data-testid={testId}
      data-die-flat
      aria-label={dieLabel(value, landed)}
      role="img"
      style={{ display: "inline-block", width: size, height: size, opacity: spent ? 0.4 : 1 }}
    >
      <span
        ref={ref}
        style={{
          position: "relative",
          display: "block",
          width: "100%",
          height: "100%",
          // Measured: this block reported clientWidth 32 against scrollWidth 34 — its 1px border sat
          // OUTSIDE the declared size. Harmless while nothing clips, but it would be cropped the
          // moment an ancestor gains `overflow:hidden`, and WebKit is what renders this lip.
          boxSizing: "border-box",
          borderRadius: r,
          transform: `rotate(${v.tilt}deg)`,
          // THE BLOCK. A die seen slightly from above-left: this darker body shows along the
          // right and bottom as real thickness, with the read face inset on top of it. All of
          // it is drawn, so nothing here can be flattened by a WebView that mishandles depth.
          background: "linear-gradient(150deg, #cfc8b6 0%, #b3ab97 60%, #9c9480 100%)",
          boxShadow: "0 4px 10px rgba(0,0,0,.55), 0 1px 0 rgba(255,255,255,.35)",
          border: "1px solid rgba(0,0,0,.28)",
          willChange: "transform",
        }}
      >
        {/* The face you actually read — inset up-and-left, which is what leaves the lip of the
            block showing along the opposite edges. */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            right: lip,
            bottom: lip,
            borderRadius: Math.max(3, r - 1),
            background: "linear-gradient(150deg, #ffffff 0%, #fbfaf4 55%, #efeadd 100%)",
            boxShadow: "inset 0 1px 2px rgba(255,255,255,.95), inset 0 -1px 2px rgba(0,0,0,.10)",
            border: "1px solid rgba(0,0,0,.14)",
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((face, i) => (
            <span
              key={face}
              ref={(el) => {
                layers.current[i] = el;
              }}
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                // Pips must be CENTRED on the face — without this they sat in the corner.
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: flying ? 0 : face === value ? 1 : 0,
              }}
            >
              <Pips value={face} size={size} />
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

interface DieProps {
  value: number;
  size: number;
  seed: string;
  throwing: boolean;
  fromBottom: boolean;
  spent?: boolean;
  /** Extra tumbles and an optional THIRD (Z) rotation axis. Omitted → the original throw. */
  extraSpin?: { turns?: number; zSpin?: number };
  /** Perspective distance as a multiple of `size`. Lower = stronger. Default 4. */
  perspectiveRatio?: number;
  /** Keep the cube on its own compositor layer at ALL times, so the FIRST throw does not pay
   *  for layer creation while it is animating. */
  keepLayer?: boolean;
  /** Shade each face as if lit from above-front, so the cube reads as a SOLID at small sizes. */
  shaded?: boolean;
  /** How far (px) the die is thrown from — scaled by the caller to the board it lands on. */
  launch?: number;
  testId?: string;
  /**
   * Always use the DRAWN die, on every platform.
   *
   * BOTH dice games set this now. The CSS cube proved to be a per-platform lottery — composited
   * flat on Telegram's Android WebView, then buggy on iOS once iOS was the only platform still
   * running it — so both take the one renderer that behaves identically everywhere. It stays an
   * opt-in flag rather than a global switch only because `DieCube` is still the documented
   * reference implementation of the real 3-D die.
   */
  alwaysDrawn?: boolean;
}

/**
 * The die. A real CSS cube where the browser genuinely renders 3-D, and a DRAWN die where it
 * does not — Telegram's Android WebView reports support and then composites the subtree flat,
 * which no capability probe can detect. See `supports-3d.ts`. A caller may also demand the
 * drawn die outright with `alwaysDrawn`, for identical results on every platform.
 * Hook-free, so the two implementations can differ freely.
 */
export function Die3D(props: DieProps): ReactNode {
  const { extraSpin, perspectiveRatio, keepLayer, shaded, alwaysDrawn, ...rest } = props;
  if (alwaysDrawn || !supports3DTransforms()) {
    return <DieFlat {...rest} spent={rest.spent ?? false} />;
  }
  return (
    <DieCube
      {...rest}
      {...(extraSpin !== undefined ? { extraSpin } : {})}
      {...(perspectiveRatio !== undefined ? { perspectiveRatio } : {})}
      {...(keepLayer !== undefined ? { keepLayer } : {})}
      {...(shaded !== undefined ? { shaded } : {})}
    />
  );
}

function DieCube({
  value,
  size,
  seed,
  throwing,
  fromBottom,
  spent = false,
  extraSpin,
  perspectiveRatio = 4,
  keepLayer = false,
  shaded = false,
  launch,
  testId,
}: DieProps): ReactNode {
  const cubeRef = useRef<HTMLSpanElement | null>(null);
  const base = throwVariance(seed);
  const v = {
    ...base,
    ...(extraSpin?.turns ? { turns: base.turns + extraSpin.turns } : {}),
    ...(extraSpin?.zSpin ? { zSpin: extraSpin.zSpin } : {}),
  };
  const rest = restOrientation(value);
  const flying = useFlyingAtMount(throwing);
  const landed = useDieLanded(flying);

  useLayoutEffect(() => {
    if (!flying) return undefined;
    const el = cubeRef.current;
    if (!el || typeof el.animate !== "function") return undefined;
    const anim = el.animate(
      throwKeyframes(value, fromBottom, { ...v, ...(launch ? { launch } : {}) }),
      {
        duration: DIE_THROW_MS,
        easing: "cubic-bezier(.2,.7,.25,1)",
        fill: "both",
      },
    );
    anim.onfinish = (): void => anim.cancel();
    return () => anim.cancel();
    // Mount-once (deliberately empty deps), like `SlideChecker`: a die's throw plays exactly when
    // its node appears. Callers key this by the roll, so a fresh roll remounts (and replays); an
    // unrelated re-render (a clock tick) must never re-throw a die that has already landed.
  }, []);

  return (
    <span
      data-testid={testId}
      aria-label={dieLabel(value, landed)}
      role="img"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        perspective: size * perspectiveRatio,
        // The resting tilt is what makes a die read as thrown rather than placed.
        transform: `rotate(${v.tilt}deg)`,
        opacity: spent ? 0.4 : 1,
        // NO `filter` here. A filter forces its subtree onto a separate rendering surface, and with
        // a `preserve-3d` cube inside that drops the whole 3-D scene off the GPU fast path — six
        // faces re-rasterised in software every frame, which is what made the throw feel laggy on a
        // real device. The die's solidity comes from the per-face shadows instead; a spent die is
        // dimmed with `opacity` (cheap) rather than `grayscale()`.
      }}
    >
      <span
        ref={cubeRef}
        data-die-cube
        style={{
          position: "relative",
          display: "block",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          // Promote the cube to its own compositor layer for the duration of the throw, so the
          // tumble is a GPU transform rather than a per-frame repaint. Dropped once it lands.
          willChange: keepLayer || flying ? "transform" : "auto",
          transform: `translate3d(0px, 0px, 0) rotateX(${rest.x}deg) rotateY(${rest.y}deg) scale(1)`,
        }}
      >
        {FACES.map((face) => (
          <span
            key={face}
            data-die-face={face}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: shaded ? FACE_SHADE[face] : "#fbfaf4",
              borderRadius: Math.max(3, Math.round(size * 0.14)),
              // ONE cheap inset (a second inset doubled the per-face raster cost for no visible
              // gain at this size) plus a hairline edge, so the cube still reads solid now that
              // the wrapper's drop-shadow filter is gone.
              boxShadow: "inset 0 2px 4px rgba(255,255,255,0.75)",
              border: "1px solid rgba(0,0,0,0.14)",
              backfaceVisibility: "hidden",
              transform: faceTransform(face, size),
            }}
          >
            <Pips value={FACE_VALUE[face]} size={size} />
          </span>
        ))}
      </span>
    </span>
  );
}
